import { baseApi } from './baseApi';

/**
 * Persons API endpoints
 * Handles CRUD operations for person management
 */
export const personsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // GET /persons - Fetch all persons with pagination
    getPersons: builder.query({
      query: ({ page, limit, personTypeId, search } = {}) => {
        const params = new URLSearchParams();
        if (page !== undefined) params.append('page', page);
        if (limit !== undefined) params.append('limit', limit);
        if (personTypeId !== undefined && personTypeId !== null) params.append('person_type_id', personTypeId);
        if (search) params.append('search', search);
        return `/persons?${params.toString()}`;
      },
      transformResponse: (response) => {
        // Handle both old format (array) and new format (object with persons and pagination)
        if (Array.isArray(response)) {
          // Old format: convert to new format
          return {
            persons: response,
            pagination: {
              current_page: 1,
              per_page: response.length,
              total: response.length,
              total_pages: 1
            }
          };
        }
        // New format: ensure persons is always an array
        return {
          persons: Array.isArray(response?.persons) ? response.persons : [],
          pagination: response?.pagination || {
            current_page: 1,
            per_page: 10,
            total: 0,
            total_pages: 0
          }
        };
      },
      providesTags: (result) =>
        result?.persons && Array.isArray(result.persons)
          ? [
              ...result.persons.map(({ person_id }) => ({ type: 'Person', id: person_id })),
              { type: 'Person', id: 'LIST' },
            ]
          : [{ type: 'Person', id: 'LIST' }],
    }),

    // GET /persons?search=query - Search persons
    searchPersons: builder.query({
      query: ({ search, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (limit) params.append('limit', limit);
        return `/persons?${params.toString()}`;
      },
      transformResponse: (response) => {
        // Handle both old format (array) and new format (object with persons and pagination)
        if (Array.isArray(response)) {
          // Old format: convert to new format
          return {
            persons: response,
            pagination: {
              current_page: 1,
              per_page: response.length,
              total: response.length,
              total_pages: 1
            }
          };
        }
        // New format: ensure persons is always an array
        return {
          persons: Array.isArray(response?.persons) ? response.persons : [],
          pagination: response?.pagination || {
            current_page: 1,
            per_page: 20,
            total: 0,
            total_pages: 0
          }
        };
      },
      providesTags: (result) =>
        result?.persons && Array.isArray(result.persons)
          ? [
              ...result.persons.map(({ person_id }) => ({ type: 'Person', id: person_id })),
              { type: 'Person', id: 'SEARCH' },
            ]
          : [{ type: 'Person', id: 'SEARCH' }],
    }),

    // GET /persons/:id - Fetch person by ID
    getPersonById: builder.query({
      query: (id) => `/persons/${id}`,
      providesTags: (result, error, id) => [{ type: 'Person', id }],
    }),

    // POST /persons - Create new person
    createPerson: builder.mutation({
      query: (personData) => ({
        url: '/persons',
        method: 'POST',
        body: personData,
      }),
      invalidatesTags: [{ type: 'Person', id: 'LIST' }],
    }),

    // PATCH /persons/:person_id - Update person
    updatePerson: builder.mutation({
      query: ({ person_id, ...personData }) => ({
        url: `/persons/${person_id}`,
        method: 'PATCH',
        body: personData,
      }),
      invalidatesTags: (result, error, { person_id }) => [
        { type: 'Person', id: person_id },
        { type: 'Person', id: 'LIST' },
      ],
    }),

    // DELETE /persons/:id - Delete person
    deletePerson: builder.mutation({
      query: (id) => ({
        url: `/persons/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Person', id },
        { type: 'Person', id: 'LIST' },
      ],
    }),

    // GET /persons/:id/tickets - Fetch Zendesk tickets for a person
    getPersonTickets: builder.query({
      query: (personId) => `/persons/${personId}/tickets`,
      providesTags: (result, error, personId) => [
        { type: 'Person', id: personId },
        { type: 'PersonTickets', id: personId },
      ],
    }),

    // GET /persons/:id/deals - Fetch Zendesk deals for a person
    getPersonDeals: builder.query({
      query: (personId) => `/persons/${personId}/deals`,
      providesTags: (result, error, personId) => [
        { type: 'Person', id: personId },
        { type: 'PersonDeals', id: personId },
        { type: 'Deal', id: 'LIST' },
      ],
    }),

    // GET /persons/:id/tasks - Fetch Zendesk tasks for a person (deal tasks + contact tasks)
    getPersonTasks: builder.query({
      query: (personId) => `/persons/${personId}/tasks`,
      providesTags: (result, error, personId) => [
        { type: 'Person', id: personId },
        { type: 'PersonTasks', id: personId },
      ],
    }),

    // POST /persons/:id/refresh-zendesk - Refresh Zendesk data (lookup Sell contact)
    refreshZendeskData: builder.mutation({
      query: (personId) => ({
        url: `/persons/${personId}/refresh-zendesk`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, personId) => [
        { type: 'Person', id: personId },
      ],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetPersonsQuery,
  useGetPersonByIdQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useSearchPersonsQuery,
  useGetPersonTicketsQuery,
  useGetPersonDealsQuery,
  useGetPersonTasksQuery,
  useRefreshZendeskDataMutation,
} = personsApi;

